<template>
  <div id="pageDealer" @click="handleClickOutside">
    <v-container fluid>
      <v-row>
        <v-col>
          <v-card class="dealer-card elevation-2">
            <!-- Header mejorado -->
            <v-toolbar color="primary" dark flat>
              <v-icon class="mr-2">mdi-store</v-icon>
              <v-toolbar-title v-if="!$vuetify.breakpoint.mdAndDown">Concesionarios</v-toolbar-title>
              <v-spacer></v-spacer>

              <!-- Búsqueda para escritorio -->
              <v-col cols="3" class="mr-3" v-if="!$vuetify.breakpoint.mdAndDown">
                <v-text-field v-model="filter.search" label="Buscar concesionario..." prepend-inner-icon="mdi-magnify"
                  hide-details clearable rounded single-line filled dense dark
                  @keyup.enter="debouncedLoadData"></v-text-field>
              </v-col>

              <!-- Búsqueda para móvil -->
              <v-tooltip top v-if="$vuetify.breakpoint.mdAndDown && !showSearch">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click.stop="toggleSearch">
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>
                </template>
                <span>Buscar</span>
              </v-tooltip>
              <v-col cols="12" v-if="showSearch && $vuetify.breakpoint.mdAndDown">
                <v-text-field ref="searchInput" v-model="filter.search" label="Buscar concesionario..."
                  prepend-inner-icon="mdi-magnify" hide-details clearable rounded single-line filled dense dark
                  @keydown.esc="showSearch = false" @keyup.enter="debouncedLoadData"></v-text-field>
              </v-col>

              <!-- Menú de filtros -->
              <filter-menu title="Filtrar concesionarios" :current-filter="currentFilter" @canceled="rollbackFilter()"
                @filtered="commitFilter()">
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="filter.name" label="Nombre" clearable outlined dense hide-details="auto"
                      class="mb-4" prepend-inner-icon="mdi-store"></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="filter.address" label="Dirección" clearable outlined dense
                      hide-details="auto" class="mb-4" prepend-inner-icon="mdi-map-marker"></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="filter.phone" label="Teléfono" clearable outlined dense hide-details="auto"
                      class="mb-4" prepend-inner-icon="mdi-phone"></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="filter.email" label="Email" clearable outlined dense hide-details="auto"
                      prepend-inner-icon="mdi-email"></v-text-field>
                  </v-col>
                </v-row>
              </filter-menu>

              <!-- Botón para agregar concesionario -->
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click="showDealerForm(null)" class="mr-2" color="white">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Agregar concesionario</span>
              </v-tooltip>

              <!-- Botón para refrescar -->
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon @click="loadData" v-on="on" class="mr-0" color="white">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Actualizar lista</span>
              </v-tooltip>
            </v-toolbar>

            <!-- Selector de vista (tarjetas/tabla) -->
            <v-card-actions class="px-4 py-2">
              <div class="text-subtitle-1 font-weight-medium grey--text text--darken-2">
                {{ totalItems }} concesionarios encontrados
              </div>
              <v-spacer></v-spacer>
              <v-btn-toggle v-model="viewMode" mandatory dense color="primary">
                <v-btn value="cards" small>
                  <v-icon small>mdi-view-grid-outline</v-icon>
                  Tarjetas
                </v-btn>
                <v-btn value="table" small>
                  <v-icon small>mdi-table</v-icon>
                  Tabla
                </v-btn>
              </v-btn-toggle>
            </v-card-actions>

            <!-- Vista de tarjetas -->
            <v-card-text v-if="viewMode === 'cards'" class="px-4 pt-0 pb-4">
              <!-- Estado de carga -->
              <v-row v-if="loadingItems">
                <v-col cols="12" sm="6" md="4" lg="3" v-for="n in 8" :key="`skeleton-${n}`">
                  <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                </v-col>
              </v-row>

              <!-- Estado sin resultados -->
              <v-row v-else-if="!DEALERS.length" class="text-center py-5">
                <v-col cols="12">
                  <h3 class="text-h5 grey--text text--darken-1 mb-2">No se encontraron concesionarios</h3>
                  <p class="text-subtitle-1 grey--text mb-6">Intenta cambiar los filtros o añade un nuevo concesionario
                  </p>
                  <v-btn color="primary" outlined @click="showDealerForm(null)">
                    <v-icon left>mdi-plus</v-icon>
                    Agregar concesionario
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Lista de concesionarios en tarjetas -->
              <v-row v-else>
                <v-col cols="12" sm="6" md="4" lg="3" v-for="dealer in DEALERS" :key="dealer.id">
                  <v-card class="dealer-card-item h-100" outlined hover @click="viewDealerDetails(dealer)">
                    <v-card-text class="pa-4">
                      <!-- Avatar y estado -->
                      <div class="d-flex mb-4">
                        <v-avatar size="56" color="blue-grey lighten-4" class="mr-3">
                          <v-icon size="32" color="blue-grey darken-1">mdi-store</v-icon>
                        </v-avatar>
                        <div>
                          <div class="subtitle-1 font-weight-bold mb-1">
                            {{ dealer.name }}
                          </div>
                        </div>
                      </div>

                      <!-- Información básica -->
                      <v-list dense class="pa-0 transparent">
                        <v-list-item class="px-0">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="grey darken-1">mdi-map-marker</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ dealer.address || 'Sin dirección' }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item class="px-0">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="grey darken-1">mdi-phone</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ dealer.phone || 'Sin teléfono' }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item class="px-0">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="grey darken-1">mdi-email</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle class="text-truncate">{{ dealer.email || 'Sin email'
                              }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-card-text>

                    <v-divider></v-divider>                    
                    <!-- Acciones de tarjeta -->
                    <v-card-actions @click.stop>
                      <v-spacer></v-spacer>
                      <v-btn icon small color="primary" @click.stop="showDealerForm(dealer)" title="Editar">
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon small color="info" :to="{ name: 'DealerDetail', params: { dealer_id: dealer.id } }"
                        title="Ver detalles" @click.stop>
                        <v-icon>mdi-eye</v-icon>
                      </v-btn>                      
                      <!-- Nuevo botón para acceder como gestor -->
                      <v-btn 
                        icon 
                        small 
                        :color="isAdminDisabled(dealer) ? 'grey' : 'orange'" 
                        @click.stop="handleSwitchToDealer(dealer, $event)" 
                        :loading="loadingSwitchId === dealer.id"
                        :disabled="loadingSwitch"
                        :title="isAdminDisabled(dealer) ? 'Administrador deshabilitado' : 'Acceder como gestor'"
                        v-if="isAdmin"
                      >
                        <v-icon>mdi-account-switch</v-icon>
                      </v-btn>
                      <v-btn icon small color="red" @click.stop="confirmDeleteDealer(dealer)" title="Eliminar">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Paginación en vista de tarjetas -->
              <div class="text-center pt-4" v-if="!loadingItems && DEALERS.length > 0">
                <v-row>
                  <v-col cols="12" class="d-flex justify-center align-center flex-wrap pa-2">
                    <div class="text-center d-flex align-center mr-4">
                      <span class="text-body-2 grey--text text--darken-1">Filas por página:</span>
                      <v-select v-model="pagination.itemsPerPage" :items="footer.itemsPerPageOptions" dense outlined
                        hide-details class="ml-2" style="width: 80px;" @change="onItemsPerPageChange"></v-select>
                    </div>

                    <v-pagination v-model="pagination.page" :length="Math.ceil(totalItems / pagination.itemsPerPage)"
                      :total-visible="7" circle @input="onPageChange"></v-pagination>

                    <span class="text-body-2 grey--text text--darken-1 ml-4">
                      {{ paginationText }}
                    </span>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>

            <!-- Vista de tabla -->
            <v-card-text v-else class="pa-0">
              <v-data-table :headers="headers" :items="DEALERS" :options.sync="pagination" :loading="loadingItems"
                :footer-props="footer" :key="tableId" item-key="id" class="elevation-0" multi-sort
                @click:row="viewDealerDetails" :server-items-length="totalItems">
                <!-- Columna de nombre -->
                <template v-slot:item.name="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="36" color="blue-grey lighten-4" class="mr-2">
                      <v-icon size="20" color="blue-grey darken-1">mdi-store</v-icon>
                    </v-avatar>
                    <div>
                      <div>{{ item.name }}</div>
                      <div class="caption grey--text" v-if="item.email">{{ item.email }}</div>
                    </div>
                  </div>
                </template>

                <!-- Columna de contacto -->
                <template v-slot:item.contact="{ item }">
                  <div>
                    <div class="d-flex align-center">
                      <v-icon small color="grey darken-1" class="mr-1">mdi-map-marker</v-icon>
                      <span>{{ item.address || 'Sin dirección' }}</span>
                    </div>
                    <div class="d-flex align-center mt-1">
                      <v-icon small color="grey darken-1" class="mr-1">mdi-phone</v-icon>
                      <span>{{ item.phone || 'Sin teléfono' }}</span>
                    </div>
                  </div>
                </template>
                <!-- Columna de acciones -->
                <template v-slot:item.action="{ item }">
                  <div class="d-inline-flex action-buttons">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn icon small color="primary" @click.stop="showDealerForm(item)" v-on="on">
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Editar</span>
                    </v-tooltip>

                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn icon small color="info" :to="{ name: 'DealerDetail', params: { dealer_id: item.id } }"
                          v-on="on" @click.stop>
                          <v-icon>mdi-eye</v-icon>
                        </v-btn>
                      </template>
                      <span>Ver detalles</span>
                    </v-tooltip>                    
                    <!-- Nuevo botón para acceder como gestor -->
                    <v-tooltip top v-if="isAdmin">
                      <template v-slot:activator="{ on }">                      
                        <v-btn 
                        icon 
                        small 
                        :color="isAdminDisabled(item) ? 'grey' : 'orange'" 
                        @click.stop="handleSwitchToDealer(item, $event)" 
                        :loading="loadingSwitchId === item.id"
                        :disabled="loadingSwitch" 
                        v-on="on">
                        <v-icon>mdi-account-switch</v-icon>
                      </v-btn>
                      </template>
                      <span>{{ isAdminDisabled(item) ? 'Administrador deshabilitado' : 'Acceder como gestor' }}</span>
                    </v-tooltip>

                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn icon small color="red" @click.stop="confirmDeleteDealer(item)" v-on="on">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Eliminar</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <!-- Formulario para crear/editar concesionario -->
          <dealer-form v-if="visibleForm" v-model="editItem" @save="updateDataTable"
            @cancel="visibleForm = false"></dealer-form>
        </v-col>
      </v-row>
    </v-container>

    <!-- Diálogo de confirmación de eliminación -->
    <v-dialog v-model="dialogDelete" max-width="500px">
      <v-card>
        <v-card-title class="headline error--text">Confirmar eliminación</v-card-title>
        <v-card-text class="py-4">
          <v-row>
            <v-col cols="auto" class="pr-0">
              <v-avatar color="error" size="48" class="mr-4">
                <v-icon dark>mdi-alert</v-icon>
              </v-avatar>
            </v-col>
            <v-col>
              <p class="mb-1">¿Estás seguro de que deseas eliminar este concesionario?</p>
              <p class="subtitle-1 font-weight-medium">{{ selectedDealer.name }}</p>
              <p class="caption grey--text" v-if="selectedDealer.address">
                Dirección: {{ selectedDealer.address }}
              </p>
            </v-col>
          </v-row>
          <p class="caption mt-3 red--text">Esta acción no se puede deshacer.</p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogDelete = false">Cancelar</v-btn>
          <v-btn color="error" text @click="deleteDealer" :loading="loadingDelete">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-overlay :value="loadingSwitch" z-index="9999">
  <v-card class="pa-3 elevation-5" max-width="350">
    <v-card-text class="text-center">
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
        class="mb-3"
      ></v-progress-circular>
      <h3 class="subtitle-1 font-weight-medium mb-2">Cambiando sesión...</h3>
      <p class="body-2 mb-0">Accediendo como gestor del concesionario</p>
    </v-card-text>
  </v-card>
</v-overlay>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import FilterMenu from '../../components/FilterMenu'
import DealerService from '../../services/DealerService'
import AuthService from '../../services/AuthService'
import UserService from '../../services/UserService'

export default {
  components: {
    FilterMenu,
    DealerForm: () => import(/* webpackPrefetch: true */ './DealerForm')
  },

  data: () => ({
    // Vista y carga
    viewMode: 'table', // Por defecto mostrar tarjetas
    loadingItems: false,
    loadingDelete: false,
    totalItems: 0,
    tableId: 0,
    showSearch: false,
    loadingSwitch: false,
    loadingSwitchId: null,

    // Tabla
    headers: [
      {
        text: 'Concesionario',
        value: 'name',
        sortable: true,
        width: '35%'
      },
      {
        text: 'Contacto',
        value: 'contact',
        sortable: false,
        width: '35%'
      },
      {
        text: 'Acciones',
        value: 'action',
        sortable: false,
        align: 'center',
        width: '150px'
      }
    ],
    footer: {
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      itemsPerPageText: 'Concesionarios por página:',
      pageText: '{0}-{1} de {2}'
    },

    // filtro
    filter: {
      search: null,
      name: null,
      address: null,
      phone: null,
      email: null,
      status: null
    },
    currentFilter: {
      search: null,
      name: null,
      address: null,
      phone: null,
      email: null,
      status: null
    },
    // form
    visibleForm: false,
    editItem: null,

    // Confirmación de eliminación
    dialogDelete: false,
    selectedDealer: {},

    // Inicialización
    isInitializing: true
  }),
  computed: {
    ...mapGetters(['DEALERS', 'CURRENT_USER_ROLES']),

    pagination: {
      get: function () {
        return this.$store.getters.DEALERS_PAG
      },
      set: function (value) {
        this.$store.commit('SET_DEALERS_PAG', value)
      }
    },

    isAdmin () {
      return this.CURRENT_USER_ROLES.includes('admin')
    },

    searchTerm () {
      return this.filter.search
    },

    paginationText () {
      const start = (this.pagination.page - 1) * this.pagination.itemsPerPage + 1
      const end = Math.min(start + this.pagination.itemsPerPage - 1, this.totalItems)
      return `${start}-${end} de ${this.totalItems}`
    },

    totalPages () {
      return Math.ceil(this.totalItems / this.pagination.itemsPerPage)
    },

    // Check if dealer's admin is disabled
    isAdminDisabled () {
      return (dealer) => {
        return dealer.admin && dealer.admin.status === 9
      }
    }
  },

  methods: {
    loadData () {
      this.loadingItems = true
      const filter = {
        ...this.filter,
        is_colaborador: 0,
        search: this.filter.search ? this.filter.search.trim() : null
      }
      this.$store.dispatch('LOAD_DEALERS', filter).then(result => {
        this.totalItems = result

        // Asegurarse de que la página actual es válida
        const maxPage = Math.ceil(result / this.pagination.itemsPerPage)
        if (this.pagination.page > maxPage && maxPage > 0) {
          this.pagination.page = maxPage
        }
        this.loadingItems = false
      }).catch(error => {
        console.error('Error al cargar concesionarios:', error)
        this.$toast.error('Error al cargar los concesionarios')
        this.loadingItems = false
      })
    },

    debouncedLoadData: _.debounce(function () {
      this.loadData()
    }, 500),

    onPageChange (page) {
      // Actualizar la página en la paginación
      this.pagination.page = page
      // Forzar la carga de datos inmediatamente después del cambio de página
      this.loadData()
    },

    onItemsPerPageChange (perPage) {
      // Actualizar items por página en la paginación
      this.pagination.itemsPerPage = perPage
      // Asegurarse de volver a la primera página al cambiar elementos por página
      this.pagination.page = 1
      // Forzar la carga de datos
      this.loadData()
    },

    updateDataTable ({ insert }) {
      this.visibleForm = false
      if (insert) {
        this.totalItems++
      }
      this.refreshDataTable()
      this.$toast.success(insert ? 'Concesionario creado con éxito' : 'Concesionario actualizado con éxito')
    },

    refreshDataTable () {
      this.tableId++
      this.loadData()
    },

    // filtro
    commitFilter () {
      for (const key in this.currentFilter) {
        this.currentFilter[key] = this.filter[key]
      }
      this.loadData()
    },

    rollbackFilter () {
      for (const key in this.currentFilter) {
        this.filter[key] = this.currentFilter[key]
      }
    },

    // acciones
    showDealerForm (item) {
      this.editItem = item ? { ...item } : null
      this.visibleForm = true
    },

    viewDealerDetails (dealer) {
      this.$router.push({
        name: 'DealerDetail',
        params: { dealer_id: dealer.id }
      })
    },

    toggleSearch () {
      this.showSearch = !this.showSearch
      if (this.showSearch) {
        this.$nextTick(() => {
          this.$refs.searchInput.focus()
        })
      }
    },

    handleClickOutside (event) {
      const searchInput = this.$refs.searchInput
      if (searchInput && !searchInput.$el.contains(event.target)) {
        this.showSearch = false
      }
    },

    confirmDeleteDealer (item) {
      this.selectedDealer = item
      this.dialogDelete = true    
    },

    deleteDealer () {
      this.loadingDelete = true
      this.$store.dispatch('DELETE_DEALER', this.selectedDealer).then(() => {
        this.dialogDelete = false
        this.loadingDelete = false
        this.$toast.success('Concesionario eliminado con éxito')
        this.refreshDataTable()
      }).catch(error => {
        console.error('Error al eliminar concesionario:', error)
        this.$toast.error('Error al eliminar el concesionario')
        this.loadingDelete = false
      })    
    },

    // Handle switch to dealer with admin status check
    handleSwitchToDealer(dealer, event) {
      // Prevent any action if admin is disabled
      if (this.isAdminDisabled(dealer)) {
        // Stop event propagation to prevent card click
        this.showAdminDisabledWarning()
        return false
      } else {
        this.switchToDealer(dealer)
      }
    },

    // Show warning when admin is disabled
    showAdminDisabledWarning() {
      this.$confirm(
        'El administrador de este concesionario está deshabilitado. Es necesario habilitarlo antes de poder cambiar a este concesionario.',
        {
          title: 'Administrador deshabilitado',
          buttonFalseText: 'Entendido',
          buttonTrueText: null,
          buttonTrueColor: 'primary',
          buttonFalseColor: 'primary',
          color: 'warning'
        }
      )
    },

    async switchToDealer(dealer) {
      try {
        this.loadingSwitch = true
        this.loadingSwitchId = dealer.id
        
        // Primero obtener el gestor del concesionario
        const response = await DealerService.view(dealer)
        const manager = await UserService.viewUser(response.data.data.admin_id)
        
        if (!manager.data.data || !manager.data.data || !manager.data.data.email) {
          this.$toast.error('No se encontró un gestor para este concesionario')
          this.loadingSwitch = false
          return
        }
        
        const managerEmail = manager.data.data.email
        // Guardar información de la sesión original ANTES de cambiar
        const originalUserInfo = this.$store.getters.CURRENT_USER
        const originalSession = {
          token: this.$store.getters.BEARER_TOKEN,
          roles: JSON.stringify(this.CURRENT_USER_ROLES),
          permissions: JSON.stringify(this.$store.getters.CURRENT_USER_PERMISSIONS || []),
          user_email: originalUserInfo.email,
          user_name: originalUserInfo.name || `${originalUserInfo.first_name} ${originalUserInfo.last_name}`
        }
        localStorage.setItem('original_session', JSON.stringify(originalSession))
        // Realizar la solicitud para cambiar de sesión
        const switchResponse = await AuthService.switchSession({
          email: managerEmail
        })
        
        if (switchResponse.data && switchResponse.data.data.access_token) {
          // Guardar la sesión original ANTES de cambiar
          const originalSession = {
            token: this.$store.getters.BEARER_TOKEN,
            roles: JSON.stringify(this.CURRENT_USER_ROLES),
            permissions: JSON.stringify(this.$store.getters.CURRENT_USER_PERMISSIONS || [])
          }
          localStorage.setItem('original_session', JSON.stringify(originalSession))
          
          // Guardar el nuevo token y actualizar la sesión
          const token = switchResponse.data.data.access_token
          
          // Actualizar solo el token (persiste en cookie via vuex-persistedstate) sin pisar
          // isPartner/partnerPermissions en localStorage antes de obtener los datos completos
          this.$store.commit('SET_TOKEN_ONLY', token)
          
          // Obtener datos completos del usuario con el nuevo token
          try {
            const userResponse = await UserService.getMe()
            const user = userResponse.data.data
            const roles = user.roles
            const permissions = user.permissions || []
            const partnerPermissions = user.partner_permissions || {}
            const isPartner = user.partner_group_member || false
            
            // Actualizar con los datos completos (incluye isPartner y partnerPermissions correctos)
            await this.$store.dispatch('SWITCH_SESSION', { token, user, roles, permissions, partnerPermissions, isPartner })
          } catch (error) {
            console.error('Error obteniendo datos completos del usuario:', error)
            // Fallback con datos básicos
            const user = manager.data.data
            const roles = manager.data.data.roles
            const permissions = manager.data.data.permissions || []
            const partnerPermissions = manager.data.data.partner_permissions || {}
            const isPartner = manager.data.data.partner_group_member || false
            await this.$store.dispatch('SWITCH_SESSION', { token, user, roles, permissions, partnerPermissions, isPartner })
          }
          
          this.$toast.success(`Has iniciado sesión como gestor de ${dealer.name}`)
          
          // Redireccionar a la página de vehículos y forzar recarga completa
          setTimeout(() => {
            window.location.href = '/vehiculo' // Redirecciona y recarga la página completamente
          }, 500) // Aumentar un poco el timeout para que el mensaje se muestre
        } else {
          this.$toast.error('No se pudo iniciar sesión como gestor')
        }
      } catch (error) {
        console.error('Error al cambiar de sesión:', error)
        this.$toast.error(error.response?.data?.message || 'Error al cambiar de sesión')
      } finally {        
        this.loadingSwitch = false
        this.loadingSwitchId = null
      }
    }
  },

  watch: {
    pagination: {
      handler (newVal, oldVal) {
        // Verificar si realmente cambió algo importante que requiera recargar
        const relevantPropsChanged = ['page', 'itemsPerPage', 'sortBy', 'sortDesc'].some(
          prop => !_.isEqual(newVal[prop], oldVal?.[prop])
        )
        // Solo recargar si hay cambios relevantes y no estamos en medio de una carga
        if (relevantPropsChanged && !this.loadingItems && !this.isInitializing) {
          this.loadData()
        }
      },
      deep: true
    },

    searchTerm () {
      this.debouncedLoadData()
    }
  },

  created () {
    // Bandera para evitar recarga durante la inicialización
    this.isInitializing = true
    this.loadData()
    // Desactivar la bandera de inicialización después de la carga inicial
    this.$nextTick(() => {
      this.isInitializing = false
    })
  }
}
</script>

<style scoped>
.dealer-card {
  border-radius: 8px;
  overflow: hidden;
}

.dealer-card-item {
  transition: box-shadow 0.2s ease-in-out, transform 0.2s ease;
}

.dealer-card-item:hover {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px);
}

.action-buttons button {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.action-buttons button:hover {
  opacity: 1;
}

.search-input-mobile {
  max-width: 200px;
}

/* Animación para nuevos elementos */
@keyframes highlight {
  0% {
    background-color: rgba(var(--v-primary-base), 0.1);
  }

  100% {
    background-color: transparent;
  }
}

.new-item {
  animation: highlight 2s ease-in-out;
}
</style>
